import { flash, SubmitButton } from "@xola/ui-kit";
import { AxiosError } from "axios";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString } from "common";
import { get } from "lodash";
import React, { useCallback } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { CreateResource } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { EditEquipmentTab } from "./types";

const validationSchema = object().shape({
    name: requiredString(),
    count: requiredNumberMoreThanZero(),
    capacity: requiredNumberMoreThanZero(),
});

const initialValues = {
    count: null,
    name: "",
    capacity: null,
};

export const CreateEquipment = () => {
    const history = useHistory();
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: createEquipment, isLoading } = useMutation(
        async (payload: CreateResource) => {
            return api.post("/resources", payload);
        },
        {
            onError: (error: AxiosError) => {
                const errorStatus = get(error, "response.status");
                if (errorStatus === 400) {
                    const data: any = error.response?.data;

                    if (data?.global && typeof data?.global?.[0] === "string") {
                        const message = data?.global?.[0];
                        flash.danger(message);
                    }
                }
            },
        },
    );

    const handleSubmit = async (values: any) => {
        const payload = { ...values, seller: { id: seller.id }, schedules: [] };
        try {
            await createEquipment(payload)
                .then(handleEdit)
                .finally(() => {
                    invalidate("resources");
                    invalidate("units");
                });
        } catch {}
    };

    const handleReturn = useCallback(() => {
        history.push(`/resources/equipment`);
    }, [history]);

    const handleEdit = useCallback(
        ({ data }) => {
            flash.success("Equipment saved");
            history.push(`/resources/equipment/${data.id}/${EditEquipmentTab.Equipment}`);
        },
        [history],
    );

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <ContentLayout isDisabled={isLoading}>
                    <FormField name="name" label="Name">
                        <InputField isRequired placeholder="Name" />
                    </FormField>

                    <FormField
                        name="count"
                        label="Count"
                        tooltip="The number of identical equipment items. For example, if you have 3 buses that hold the exact same number of people, set the count to 3."
                    >
                        <InputField isRequired type="number" />
                    </FormField>

                    <FormField
                        name="capacity"
                        label="Capacity"
                        tooltip="The number of spots a single equipment item can accommodate. For example, a bus with 10 seats should have a capacity of 10."
                    >
                        <div className="space-between align-center flex">
                            <span className="w-full self-center">Each of this item can hold up to</span>
                            <InputField isRequired type="number" />
                        </div>
                    </FormField>

                    <ContentLayout.Footer>
                        <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>

                        <SecondaryButton type="reset" disabled={isLoading} onClick={handleReturn}>
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
